import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class PromotionTimer extends UcoastEl {
	static htmlSelector = 'promotion-timer'
	endDateRaw: string | null
	endDate: number | null = null
	timerInterval: number | null = null
	showDays: boolean
	hasExpired = false
	
	daysEl!: HTMLElement
	hoursEl!: HTMLElement
	minutesEl!: HTMLElement
	secondsEl!: HTMLElement
	statusEl: HTMLElement | null = null
	visibilityHandler: () => void
	intersectionObserver: IntersectionObserver | null = null
	lastAnnouncedMinute: number | null = null

	constructor() {
		super()
		this.endDateRaw = this.getAttribute('data-end-date')
		this.showDays = this.getAttribute('data-show-days') !== 'false'
		this.visibilityHandler = () => this.syncTimerInterval()
		if (!this.endDateRaw) {
			const section = this.closest<HTMLElement>('[data-offer-duration-days]')
			const durationDays = Number(section?.dataset.offerDurationDays || 7)
			const storageKey = 'pvolve:fitness-quiz:offer-expires-at'
			let expiresAt = Number(window.localStorage.getItem(storageKey))
			if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
				expiresAt = Date.now() + durationDays * 24 * 60 * 60 * 1000
				window.localStorage.setItem(storageKey, String(expiresAt))
			}
			this.endDate = expiresAt
		}
		if (this.endDateRaw) {
			const parsedDate = new Date(this.endDateRaw).getTime()
			if (!isNaN(parsedDate)) {
				this.endDate = parsedDate
			}
		}
	}

	override connectedCallback() {
		super.connectedCallback()
		this.daysEl = q.rs('[data-timer-days]', this)
		this.hoursEl = q.rs('[data-timer-hours]', this)
		this.minutesEl = q.rs('[data-timer-minutes]', this)
		this.secondsEl = q.rs('[data-timer-seconds]', this)
		this.statusEl = this.querySelector<HTMLElement>('[data-timer-status]')
		if (this.endDate) {
			this.updateTimer()
			this.syncTimerInterval()
			document.addEventListener('visibilitychange', this.visibilityHandler)
			if ('IntersectionObserver' in window) {
				this.intersectionObserver = new IntersectionObserver(() => this.syncTimerInterval(), { rootMargin: '200px 0px' })
				this.intersectionObserver.observe(this)
			}
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		document.removeEventListener('visibilitychange', this.visibilityHandler)
		this.intersectionObserver?.disconnect()
		this.stopTimer()
	}

	private isVisible() {
		return !document.hidden && this.offsetParent !== null && !this.closest<HTMLElement>('[hidden]')
	}

	private stopTimer() {
		if (this.timerInterval) clearInterval(this.timerInterval)
		this.timerInterval = null
	}

	private syncTimerInterval() {
		this.stopTimer()
		if (!this.endDate || !this.isVisible() || this.hasExpired) return
		this.updateTimer()
		this.timerInterval = window.setInterval(() => this.updateTimer(), 1_000)
	}

	updateTimer() {
		if (!this.endDate) return

		const now = new Date().getTime()
		const distance = this.endDate - now

		if (distance < 0) {
			if (this.timerInterval) {
				clearInterval(this.timerInterval)
			}
			this.daysEl.textContent = '00'
			this.hoursEl.textContent = '00'
			this.minutesEl.textContent = '00'
			this.secondsEl.textContent = '00'
			if (!this.hasExpired) {
				this.hasExpired = true
				this.dispatchEvent(new CustomEvent('promotion-timer:expired', { bubbles: true }))
			}
			return
		}

		const days = Math.floor(distance / (1000 * 60 * 60 * 24))
		const hours = this.showDays
			? Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
			: Math.floor(distance / (1000 * 60 * 60))
		const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
		const seconds = Math.floor((distance % (1000 * 60)) / 1000)

		this.daysEl.textContent = days.toString().padStart(2, '0')
		this.hoursEl.textContent = hours.toString().padStart(2, '0')
		this.minutesEl.textContent = minutes.toString().padStart(2, '0')
		this.secondsEl.textContent = seconds.toString().padStart(2, '0')
		const totalMinutes = Math.floor(distance / 60_000)
		if (this.statusEl && totalMinutes !== this.lastAnnouncedMinute) {
			this.statusEl.textContent = `${days} days, ${hours} hours, ${minutes} minutes remaining`
			this.lastAnnouncedMinute = totalMinutes
		}
	}
}

customElements.define('promotion-timer', PromotionTimer)
